/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './Menu.scss';

/**
 * This is the Menu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Menu({ open, items, languages, onFocus, labels }) {
  return (
    <div
      id="menu"
      role="tabpanel"
      aria-labelledby="menubutton"
      aria-hidden={!open}
      className={classnames('content-menu', { open, 'animate-visible': open })}
      tabIndex={open ? null : -1}
    >
      <div role="menu">
        <div className="page-padding items">
          {items.map(({ id, href, title }, i) => (
            <a
              className="animate-stagger"
              role="menuitem"
              key={id}
              href={href}
              tabIndex={open ? null : -1}
              onFocus={() => {
                onFocus('menu');
              }}
              style={{
                animationDelay: `${0.1 + i * 0.08}s`,
              }}
            >
              {title}
            </a>
          ))}
        </div>
        <footer>
          <div className="links animate-1">
            {/* <a
              tabIndex={open ? null : -1}
              role="menuitem"
              href={about.href}
              dangerouslySetInnerHTML={{ __html: about.title }}
            />
            <a
              tabIndex={open ? null : -1}
              role="menuitem"
              href={privacy.href}
              dangerouslySetInnerHTML={{ __html: privacy.title }}
            /> */}
          </div>
          <div
            className="languages animate-2"
            role="group"
            aria-label={labels.languages}
          >
            {languages.map(({ id, code, short, text, url, active }) => (
              <span key={id}>
                <a
                  className={classnames({ active })}
                  href={url}
                  role="menuitem"
                  lang={code}
                  title={text}
                  aria-label={text}
                  aria-hidden={active}
                  tabIndex={open ? null : -1}
                >
                  <span>{short}</span>
                </a>
              </span>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Menu.propTypes = {
  open: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      href: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  about: PropTypes.shape({
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  privacy: PropTypes.shape({
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      short: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
    })
  ).isRequired,
  onFocus: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    languages: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Menu.defaultProps = {
  contact: 'Contact',
  items: [],
  languages: [],
  onFocus: () => {},
  onContact: () => {},
  labels: {
    languages: 'Languages',
  },
};

export default Menu;
